// Fixed research rules, not learned probabilities. No outcomes enter this model.
export const VERSION = "wheel-screen-v1";
export const DEFAULTS = Object.freeze({minimumScore:60, capital:100000, nameCap:.30, sectorCap:.50, totalCap:.70});
export const WEIGHTS = Object.freeze({volatility:30, trend:25, stability:25, liquidity:20});
const finite = Number.isFinite;
const clip = x => Math.max(0, Math.min(1,x));
export function evaluate(row, minimumScore=60) {
  const f=row.features,q=row.contract;
  const issues=[...(row.dataIssues||[])];
  if(!f||!q||![f.rv20,f.rv5Ratio,f.return5,f.ma50Distance,f.drawdown20,f.worstDay20,
    q.ivRvRatio,q.relativeSpread,q.oi,q.delta,q.shortGamma,q.cashReserve,q.creditYield,q.dte].every(finite)
    ||q.cashReserve<=0||f.rv20<=0){
    issues.push("Complete stock and option inputs required");
  }
  if(issues.length)return {...row,status:"data",score:null,parts:null,reasons:[...new Set(issues)]};
  const parts={
    volatility:30*clip((q.ivRvRatio-.9)/.6),
    trend:25*clip((f.ma50Distance+.08)/.16),
    stability:25*(.5*clip((1.6-f.rv5Ratio)/.8)+.5*clip((f.worstDay20+.08)/.06)),
    liquidity:20*(.6*clip((.15-q.relativeSpread)/.14)+.4*clip(Math.log(q.oi/500)/Math.log(20))),
  };
  const score=Object.values(parts).reduce((a,b)=>a+b,0),reasons=[];
  if(q.oi<500||q.relativeSpread>.15||q.delta<.15||q.delta>.35||q.dte<28||q.dte>42||!q.callMarketChecked)reasons.push("Contract liquidity / tenor gate failed");
  if(q.ivRvRatio<1.05)reasons.push("IV is less than 1.05× trailing 20-day RV");
  if(f.return5<-.08)reasons.push("Stock fell more than 8% in five sessions");
  if(f.drawdown20<-.15)reasons.push("Stock is more than 15% below its 20-session high");
  if(f.rv5Ratio>1.5)reasons.push("Five-day RV is more than 1.5× twenty-day RV");
  if(score<minimumScore)reasons.push(`Suitability score below ${minimumScore}`);
  return {...row,score,parts,reasons,status:reasons.length?"avoid":"candidate"};
}

export function validateSettings(s) {
  return finite(s.capital)&&s.capital>=1000&&s.capital<=1000000000
    &&finite(s.minimumScore)&&s.minimumScore>=0&&s.minimumScore<=100
    &&[s.nameCap,s.sectorCap,s.totalCap].every(v=>finite(v)&&v>0&&v<=1);
}

export function portfolio(snapshot, picks, settings=DEFAULTS) {
  const rows=[],issues=[],seen=new Set();
  if(!validateSettings(settings))return {rows,issues:["Invalid capital or risk settings"],reserve:0,remaining:0,correlations:[],gamma:null,delta:null};
  for(const pick of picks){
    if(seen.has(pick.symbol)){issues.push(`Duplicate ${pick.symbol} position`);continue;}
    seen.add(pick.symbol);
    const row=snapshot.candidates.find(r=>r.symbol===pick.symbol);
    if(!row||!Number.isInteger(pick.quantity)||pick.quantity<1||pick.quantity>10000){issues.push("Invalid pool item");continue;}
    const assessed=evaluate(row,settings.minimumScore),q=row.contract;
    if(assessed.status!=="candidate")issues.push(`${row.symbol}: no longer passes this screen`);
    if(!q){issues.push(`${row.symbol}: no selected contract`);continue;}
    const reserve=q.cashReserve*pick.quantity;
    if(reserve>settings.capital*settings.nameCap+1e-8)issues.push(`${row.symbol}: above ${(settings.nameCap*100).toFixed(0)}% single-name cash cap`);
    rows.push({...assessed,quantity:pick.quantity,reserve});
  }
  const reserve=rows.reduce((a,r)=>a+r.reserve,0),sectors={};
  rows.forEach(r=>sectors[r.sector]=(sectors[r.sector]||0)+r.reserve);
  if(reserve>settings.capital*settings.totalCap+1e-8)issues.push(`Above ${(settings.totalCap*100).toFixed(0)}% total cash commitment cap`);
  for(const [sector,value] of Object.entries(sectors))if(value>settings.capital*settings.sectorCap+1e-8)issues.push(`${sector}: above ${(settings.sectorCap*100).toFixed(0)}% sector cash cap`);
  const correlations=(snapshot.correlations||[]).filter(p=>seen.has(p.left)&&seen.has(p.right));
  for(let i=0;i<rows.length;i++)for(let j=i+1;j<rows.length;j++){
    const pair=correlations.find(p=>[p.left,p.right].includes(rows[i].symbol)&&[p.left,p.right].includes(rows[j].symbol));
    if(!pair||!finite(pair.correlation))issues.push(`${rows[i].symbol} / ${rows[j].symbol}: correlation unavailable`);
    else if(pair.correlation>.8)issues.push(`${rows[i].symbol} / ${rows[j].symbol}: 60-session correlation above 0.80`);
  }
  return {rows,issues,reserve,sectors,correlations,remaining:settings.capital-reserve,
    gamma:rows.reduce((s,r)=>s+r.contract.shortGamma*r.quantity,0),
    delta:rows.reduce((s,r)=>s+100*r.contract.delta*r.quantity,0)};
}

export function addToPool(snapshot,picks,pick,settings=DEFAULTS){
  const next=[...picks.filter(p=>p.symbol!==pick.symbol),pick];
  const result=portfolio(snapshot,next,settings);
  return {ok:result.issues.length===0,picks:result.issues.length?picks:next,...result};
}

export function restorePool(raw, data){
  try{
    const value=JSON.parse(raw);
    if(value.version!==VERSION||!validateSettings(value.settings)||!Array.isArray(value.picks)
      ||!data.snapshots.some(s=>s.date===value.date))return null;
    const seen=new Set();
    if(value.picks.length>data.universe.length||value.picks.some(p=>!data.universe.includes(p.symbol)
      ||!Number.isInteger(p.quantity)||p.quantity<1||p.quantity>10000||seen.has(p.symbol)||(seen.add(p.symbol),false)))return null;
    return value;
  }catch{return null;}
}
